import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

@Injectable({
  providedIn: 'root'
})
export class PdfExportService {

  constructor() { }

  async exportToPdf(elementSelectors: string | string[], filename: string = 'store-details.pdf'): Promise<void> {
    try {
      window.scrollTo(0, 0);
      await new Promise(resolve => setTimeout(resolve, 1000));

      const pdf = new jsPDF('p', 'mm', 'a4');


      // Default selectors if none provided
      const defaultSelectors = [
        '.store-header-panel-container',
        '.store-detail-content'
      ];

      const selectors = Array.isArray(elementSelectors) ? elementSelectors :
                       (elementSelectors ? [elementSelectors] : defaultSelectors);

      for (const selector of selectors) {
        if (!selector || selector.trim() === '') continue;

        const element = document.querySelector(selector) as HTMLElement;
        if (!element) {
          console.log(`Element not found: ${selector}`);
          continue;
        }

        console.log(`Capturing element: ${selector}`);

        // Simple demographic flex fix only
        let originalDisplay = '';
        if (selector === 'mtn-store-demographic-profile-panel') {
          const flexContainer = element.querySelector('.presentation-container.container.ng-star-inserted') as HTMLElement;
          if (flexContainer) {
            originalDisplay = flexContainer.style.display;
            flexContainer.style.display = 'flex';
            flexContainer.style.flexWrap = 'wrap';
          }
        }

        const canvas = await html2canvas(element, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          width: element.scrollWidth,
          height: element.scrollHeight,
          windowWidth: element.scrollWidth,
          windowHeight: element.scrollHeight,
          x: 0,
          y: 0
        });

        // Restore original display
        if (selector === 'mtn-store-demographic-profile-panel') {
          const flexContainer = element.querySelector('.presentation-container.container.ng-star-inserted') as HTMLElement;
          if (flexContainer) {
            flexContainer.style.display = originalDisplay;
            flexContainer.style.flexWrap = '';
          }
        }

        if (canvas.width > 0 && canvas.height > 0) {
          const imgData = canvas.toDataURL('image/jpeg', 0.95);
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();
          pdf.addImage(imgData, 'JPEG', 0, 0, pageWidth, pageHeight);
          console.log(`Added ${selector} to PDF`);
        } else {
          console.log(`Canvas has zero dimensions for ${selector}`);
        }
      }

      pdf.save(filename);
      console.log('PDF saved successfully');

    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }

  async exportToPdfWithPages(pageSelectors: string[][], filename: string = 'store-details.pdf'): Promise<void> {
    try {
      window.scrollTo(0, 0);
      await new Promise(resolve => setTimeout(resolve, 100));

      const pdf = new jsPDF('p', 'mm', 'letter');
      let isFirstPage = true;

      for (const selectors of pageSelectors) {
        if (!isFirstPage) {
          pdf.addPage();
        }
        isFirstPage = false;



        for (const selector of selectors) {
          if (!selector || selector.trim() === '') continue;

          const element = document.querySelector(selector) as HTMLElement;
          if (!element) {
            console.log(`Element not found: ${selector}`);
            continue;
          }

          console.log(`Capturing element: ${selector}`, element.offsetWidth, 'x', element.offsetHeight);

          // Ensure element is visible
          const originalVisibility = element.style.visibility;
          const originalDisplay = element.style.display;
          element.style.visibility = 'visible';
          element.style.display = 'block';

          // Check for page break comments and split content
          const pageBreakComments = this.findPageBreakComments(element);

          if (pageBreakComments.length > 0) {
            // Handle multi-page content
            await this.handleMultiPageContent(pdf, element, pageBreakComments);
          } else {
            // Single page content
            const canvas = await html2canvas(element, {
              backgroundColor: '#ffffff',
              scale: 2.0,
              useCORS: true,
              allowTaint: true,
              logging: false,
              width: element.scrollWidth,
              height: element.scrollHeight,
              windowWidth: element.scrollWidth,
              windowHeight: element.scrollHeight,
              x: 0,
              y: 0,
              onclone: (clonedDoc) => {
                this.prepareClonedDocument(clonedDoc);
              }
            });

            // Restore original visibility
            element.style.visibility = originalVisibility;
            element.style.display = originalDisplay;

            if (canvas.width > 0 && canvas.height > 0) {
              const imgData = canvas.toDataURL('image/jpeg', 0.92);
              console.log(`Canvas created: ${canvas.width}x${canvas.height}`);

              // Use full page size for all content
              const pageWidth = pdf.internal.pageSize.getWidth();
              const pageHeight = pdf.internal.pageSize.getHeight();
              pdf.addImage(imgData, 'JPEG', 0, 0, pageWidth, pageHeight);


              console.log(`Added ${selector} to PDF (full page)`);
            } else {
              console.error(`Canvas has zero dimensions for ${selector}`);
            }
          }

          // Restore original visibility
          element.style.visibility = originalVisibility;
          element.style.display = originalDisplay;
        }
      }

      pdf.save(filename);
      console.log('PDF saved successfully');

    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }

  private findPageBreakComments(element: HTMLElement): Comment[] {
    const comments: Comment[] = [];
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_COMMENT
    );

    let node;
    while (node = walker.nextNode()) {
      const comment = node as Comment;
      if (comment.textContent?.includes('second page of pdf') ||
          comment.textContent?.includes('page break') ||
          comment.textContent?.includes('new page')) {
        comments.push(comment);
      }
    }

    return comments;
  }

  private async handleMultiPageContent(pdf: any, element: HTMLElement, pageBreakComments: Comment[]): Promise<void> {
    console.log(`Found ${pageBreakComments.length} page break comments`);

    // Create first page (content before first page break)
    const firstPageContent = this.getContentBeforePageBreak(element, pageBreakComments[0]);
    if (firstPageContent) {
      console.log('Creating first page content');
      const canvas1 = await this.captureElement(firstPageContent);
      if (canvas1.width > 0 && canvas1.height > 0) {
        const imgData1 = canvas1.toDataURL('image/png', 1.0);
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        pdf.addImage(imgData1, 'PNG', 0, 0, pageWidth, pageHeight);


        console.log('Added first page to PDF');
      }
      document.body.removeChild(firstPageContent);
    }

    // Create subsequent pages
    for (let i = 0; i < pageBreakComments.length; i++) {
      console.log(`Creating page ${i + 2}`);
      pdf.addPage();

      const nextComment = pageBreakComments[i + 1];
      const pageContent = this.getPageContent(element, pageBreakComments[i], nextComment);

      if (pageContent) {
        console.log('Page content created, capturing...');
        const canvas = await this.captureElement(pageContent);
        if (canvas.width > 0 && canvas.height > 0) {
          const imgData = canvas.toDataURL('image/png', 1.0);
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();
          pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, pageHeight);


          console.log(`Added page ${i + 2} to PDF`);
        } else {
          console.log('Canvas has zero dimensions for page', i + 2);
        }
        document.body.removeChild(pageContent);
      } else {
        console.log('No page content created for page', i + 2);
      }
    }
  }

  private getContentBeforePageBreak(element: HTMLElement, pageBreakComment: Comment): HTMLElement | null {
    const tempDiv = document.createElement('div');
    tempDiv.className = 'pdf-temp-page';
    tempDiv.style.width = '612px';
    tempDiv.style.height = '792px';
    tempDiv.style.position = 'absolute';
    tempDiv.style.top = '-9999px';
    tempDiv.style.left = '-9999px';
    tempDiv.style.backgroundColor = '#ffffff';

    this.walkAndCloneUntil(element, pageBreakComment, tempDiv);

    document.body.appendChild(tempDiv);
    return tempDiv;
  }

  private getPageContent(element: HTMLElement, startComment: Comment, endComment?: Comment): HTMLElement | null {
    const tempDiv = document.createElement('div');
    tempDiv.className = 'pdf-temp-page';
    tempDiv.style.width = '612px';
    tempDiv.style.height = '792px';
    tempDiv.style.position = 'absolute';
    tempDiv.style.top = '-9999px';
    tempDiv.style.left = '-9999px';
    tempDiv.style.backgroundColor = '#ffffff';

    this.walkAndCloneFrom(element, startComment, endComment, tempDiv);

    document.body.appendChild(tempDiv);
    return tempDiv;
  }

  private walkAndCloneUntil(parent: Node, stopAt: Node, container: HTMLElement): void {
    let currentNode = parent.firstChild;
    while (currentNode) {
      if (currentNode === stopAt) break;

      if (currentNode.nodeType === Node.ELEMENT_NODE) {
        container.appendChild(currentNode.cloneNode(true));
      } else if (currentNode.nodeType === Node.TEXT_NODE && currentNode.textContent?.trim()) {
        container.appendChild(currentNode.cloneNode(true));
      }

      currentNode = currentNode.nextSibling;
    }
  }

  private walkAndCloneFrom(parent: Node, startFrom: Node, stopAt: Node | undefined, container: HTMLElement): void {
    let started = false;
    let currentNode = parent.firstChild;

    while (currentNode) {
      if (currentNode === startFrom) {
        started = true;
        currentNode = currentNode.nextSibling;
        continue;
      }

      if (stopAt && currentNode === stopAt) break;

      if (started) {
        if (currentNode.nodeType === Node.ELEMENT_NODE) {
          container.appendChild(currentNode.cloneNode(true));
        } else if (currentNode.nodeType === Node.TEXT_NODE && currentNode.textContent?.trim()) {
          container.appendChild(currentNode.cloneNode(true));
        }
      }

      currentNode = currentNode.nextSibling;
    }
  }

  private async captureElement(element: HTMLElement): Promise<HTMLCanvasElement> {
    return await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2.0,
      useCORS: true,
      allowTaint: true,
      logging: false,
      onclone: (clonedDoc) => {
        this.prepareClonedDocument(clonedDoc);
      }
    });
  }



  private prepareClonedDocument(clonedDoc: Document): void {
    console.log('Preparing cloned document for PDF capture');

    clonedDoc.body.style.backgroundColor = '#ffffff';

    const allElements = clonedDoc.querySelectorAll('*') as NodeListOf<HTMLElement>;
    allElements.forEach(el => {
      el.style.visibility = 'visible';
      el.style.display = el.style.display === 'none' ? 'block' : el.style.display;

      if (el.tagName === 'I' || el.tagName === 'SVG' ||
          el.classList.contains('icon') || el.classList.contains('mat-icon') ||
          (el.className && el.className.toString().includes('fa-'))) {
        el.style.color = '#000000';
        el.style.fill = '#000000';
        el.style.stroke = '#000000';
      }
    });

    const svgPaths = clonedDoc.querySelectorAll('svg path, svg circle, svg rect') as NodeListOf<SVGElement>;
    svgPaths.forEach(path => {
      if (!path.getAttribute('fill') || path.getAttribute('fill') === 'none') {
        path.style.fill = '#000000';
      }
      if (!path.getAttribute('stroke')) {
        path.style.stroke = '#000000';
      }
    });
  }

}
